import React, { useState, useEffect } from 'react';

/**
 * PWAInstallPrompt Component
 * 
 * Handles "Add to Home Screen" functionality for both Android and iOS
 * 
 * ANDROID/CHROME:
 * - Captures the `beforeinstallprompt` event
 * - Shows custom UI when appropriate
 * - Triggers the browser's native install dialog
 * 
 * iOS/SAFARI:
 * - Detects iOS Safari
 * - Shows manual instructions (iOS doesn't support programmatic install)
 * - Guides user through share menu process
 * 
 * Usage:
 *   <PWAInstallPrompt />
 */

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Detect if running in standalone mode (already installed)
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    
    setIsStandalone(standalone);
    
    if (standalone) {
      console.log('[PWA] App is running in standalone mode (already installed)');
      return;
    }

    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);
    
    // Check if user has dismissed the prompt before
    const promptDismissed = localStorage.getItem('pwa-install-dismissed');
    const dismissedTime = localStorage.getItem('pwa-install-dismissed-time');
    const daysSinceDismissed = dismissedTime 
      ? (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24) 
      : 999;

    // Don't show if dismissed within last 7 days
    if (promptDismissed === 'true' && daysSinceDismissed < 7) {
      console.log('[PWA] Install prompt dismissed recently, waiting...');
      return;
    }

    // ANDROID/CHROME: Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      console.log('[PWA] beforeinstallprompt event fired');
      
      // Prevent the default browser prompt
      e.preventDefault();
      
      // Store the event for later use
      setDeferredPrompt(e);
      
      // Show our custom prompt after a short delay (better UX)
      setTimeout(() => {
        setShowPrompt(true);
      }, 3000); // Wait 3 seconds after page load
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // IOS: Show instructions if on iOS and not installed
    if (iOS && !standalone) {
      setTimeout(() => {
        setShowIOSInstructions(true);
      }, 5000); // Wait 5 seconds for iOS users
    }

    // Listen for successful install
    const handleAppInstalled = () => {
      console.log('[PWA] App was installed successfully');
      setShowPrompt(false);
      setShowIOSInstructions(false);
      localStorage.setItem('pwa-installed', 'true');
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    // Cleanup
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Handle install button click (Android/Chrome)
  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      console.log('[PWA] No deferred prompt available');
      return;
    }

    console.log('[PWA] User clicked install, showing prompt...');
    
    // Show the browser's install prompt
    deferredPrompt.prompt();
    
    // Wait for user response
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`[PWA] User choice: ${outcome}`);
    
    if (outcome === 'accepted') {
      console.log('[PWA] ✅ User accepted the install prompt');
      localStorage.setItem('pwa-installed', 'true');
    } else {
      console.log('[PWA] ❌ User dismissed the install prompt');
      localStorage.setItem('pwa-install-dismissed', 'true');
      localStorage.setItem('pwa-install-dismissed-time', Date.now().toString());
    }
    
    // Clear the prompt
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  // Handle dismiss
  const handleDismiss = () => {
    setShowPrompt(false);
    setShowIOSInstructions(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
    localStorage.setItem('pwa-install-dismissed-time', Date.now().toString());
    console.log('[PWA] Install prompt dismissed by user');
  };

  // Don't show anything if already installed
  if (isStandalone) {
    return null;
  }

  // ANDROID/CHROME PROMPT
  if (showPrompt && deferredPrompt && !isIOS) {
    return (
      <div className="fixed bottom-4 left-4 right-4 z-[99999] animate-slideUp">
        <div className="bg-gradient-to-r from-[var(--pbfs-navy)] to-[var(--pbfs-blue)] backdrop-blur-md rounded-2xl p-4 shadow-2xl border border-white/20 max-w-md mx-auto">
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 w-6 h-6 bg-white/10 hover:bg-white/20 rounded-full text-white flex items-center justify-center text-sm transition-colors"
            aria-label="Dismiss"
          >
            ✕
          </button>
          
          {/* Icon */}
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <img 
                src="/images/pye-tag-reader-app-icon2.png" 
                alt="TagQuest Icon"
                className="w-12 h-12 rounded-xl"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
            
            {/* Content */}
            <div className="flex-1 pr-6">
              <h3 className="text-white font-bold text-base mb-1">
                📲 Install TagQuest App
              </h3>
              <p className="text-white/80 text-xs mb-3">
                Add to your home screen for quick access and offline use!
              </p>
              
              {/* Buttons */}
              <div className="flex space-x-2">
                <button
                  onClick={handleInstallClick}
                  className="flex-1 bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/90 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] text-sm shadow-lg"
                >
                  Install Now
                </button>
                <button
                  onClick={handleDismiss}
                  className="px-3 text-white/70 hover:text-white text-sm transition-colors"
                >
                  Later
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // IOS INSTRUCTIONS
  if (showIOSInstructions && isIOS) {
    return (
      <div className="fixed bottom-4 left-4 right-4 z-[99999] animate-slideUp">
        <div className="bg-gradient-to-r from-[var(--pbfs-navy)] to-[var(--pbfs-blue)] backdrop-blur-md rounded-2xl p-4 shadow-2xl border border-white/20 max-w-md mx-auto">
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 w-6 h-6 bg-white/10 hover:bg-white/20 rounded-full text-white flex items-center justify-center text-sm transition-colors"
            aria-label="Dismiss"
          >
            ✕
          </button>
          
          {/* Icon */}
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <img 
                src="/images/pye-tag-reader-app-icon2.png" 
                alt="TagQuest Icon"
                className="w-12 h-12 rounded-xl"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
            
            {/* Content */}
            <div className="flex-1 pr-6">
              <h3 className="text-white font-bold text-base mb-1">
                📲 Add to Home Screen
              </h3>
              <p className="text-white/80 text-xs mb-3">
                Install TagQuest for easy access and offline use:
              </p>
              
              {/* iOS Instructions */}
              <ol className="text-white/90 text-xs space-y-1.5 mb-3">
                <li className="flex items-start">
                  <span className="font-bold mr-2">1.</span>
                  <span>Tap the <span className="font-bold">Share</span> button 
                    <svg className="inline w-3 h-3 mx-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"/>
                    </svg>
                    at the bottom of your screen
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="font-bold mr-2">2.</span>
                  <span>Scroll down and tap <span className="font-bold">"Add to Home Screen"</span></span>
                </li>
                <li className="flex items-start">
                  <span className="font-bold mr-2">3.</span>
                  <span>Tap <span className="font-bold">"Add"</span> in the top right</span>
                </li>
              </ol>
              
              {/* Got It button */}
              <button
                onClick={handleDismiss}
                className="w-full bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/90 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] text-sm shadow-lg"
              >
                Got It!
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Don't show anything if conditions not met
  return null;
};

export default PWAInstallPrompt;
